import { useState } from "react";

import { IntakePreviewBody } from "@/features/admin/clinical-admin/IntakePreviewPanel";
import { useClinicalPatientIntakePreview } from "@/lib/api/clinicalAdminHooks";

/** What the clinician saw for a Missing-info flag: programmatic checks,
 *  highlights, and the labeled answers of the intake tied to the flagged
 *  order. Ops has no clinician-portal access, so this is how they learn what
 *  to ask the patient for. Collapsed by default; fetches on first open. */
export function MissingInfoIntakeContext({
  patientId,
  orderId,
}: {
  patientId: string;
  orderId: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const preview = useClinicalPatientIntakePreview(
    open ? patientId : null,
    orderId,
  );

  return (
    <section className="border border-line-subtle bg-raised">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="w-full flex items-center justify-between gap-8 px-12 py-9 type-label-sm hover:bg-sunken text-left"
      >
        <span className="font-medium">Intake context</span>
        <span className="text-accent">
          {open ? "Hide intake context" : "Show intake context"}
        </span>
      </button>
      {open && (
        <div className="px-12 pb-12 flex flex-col gap-10">
          {preview.isLoading ? (
            <p className="type-label-sm text-ink-muted">Loading intake…</p>
          ) : preview.isError || !preview.data ? (
            <p className="type-label-sm text-danger">
              Could not load the patient's intake.
            </p>
          ) : (
            <IntakePreviewBody
              forms={preview.data.intake_forms}
              relevantIds={preview.data.relevant_intake_form_ids}
              showAll={showAll}
              onToggleAll={() => setShowAll((current) => !current)}
            />
          )}
        </div>
      )}
    </section>
  );
}
